import { getDefaultFilter } from '@refinedev/antd'
import { type CrudFilters, usePermissions } from '@refinedev/core'
import { Form, type FormProps, Input, Select, Space } from 'antd'
import { useCallback, useRef } from 'react'

import { hasPermission } from '@/auth/capabilities'
import { USER_ROLES } from '@/constants/roles'
import { ACCOUNT_STATUSES, PAYMENT_TYPES } from '@/constants/statuses'
import { useRoles } from '@/pages/roles/hooks/useRoles'

interface UsersFilterFormProps {
  searchFormProps: FormProps
  filters: CrudFilters
}

export function UsersFilterForm({
  searchFormProps,
  filters,
}: UsersFilterFormProps) {
  const { data: permissions = [] } = usePermissions<string[]>({})
  const canReadRoles = hasPermission(permissions, 'admin:roles:read')
  const { roles, isLoading, error } = useRoles(canReadRoles)
  const roleNames = canReadRoles
    ? roles.map((role) => role.name)
    : [...USER_ROLES]
  const debounceTimer = useRef<ReturnType<typeof setTimeout>>()
  const debouncedSubmit = useCallback(() => {
    clearTimeout(debounceTimer.current)
    debounceTimer.current = setTimeout(() => {
      searchFormProps.form?.submit()
    }, 300)
  }, [searchFormProps.form])

  return (
    <Form
      {...searchFormProps}
      layout="inline"
      style={{ marginBottom: 16 }}
      initialValues={{
        search:
          getDefaultFilter('email', filters, 'eq') ??
          getDefaultFilter('ids', filters, 'eq'),
      }}
    >
      <Space wrap>
        <Form.Item name="search" noStyle>
          <Input
            placeholder="Search by email or ID..."
            style={{ width: 250 }}
            onChange={debouncedSubmit}
            allowClear
          />
        </Form.Item>
        <Form.Item name="accountStatus" noStyle>
          <Select
            placeholder="Status"
            style={{ width: 140 }}
            allowClear
            onChange={() => searchFormProps.form?.submit()}
            options={ACCOUNT_STATUSES.map((s) => ({ label: s, value: s }))}
          />
        </Form.Item>
        <Form.Item name="paymentType" noStyle>
          <Select
            placeholder="Payment"
            style={{ width: 130 }}
            allowClear
            onChange={() => searchFormProps.form?.submit()}
            options={PAYMENT_TYPES.map((t) => ({ label: t, value: t }))}
          />
        </Form.Item>
        <Form.Item name="roleName" noStyle>
          <Select
            placeholder="Role"
            style={{ width: 160 }}
            allowClear
            onChange={() => searchFormProps.form?.submit()}
            loading={canReadRoles && isLoading}
            status={canReadRoles && error ? 'error' : undefined}
            notFoundContent={
              canReadRoles && error ? 'Failed to load roles' : undefined
            }
            options={roleNames.map((role) => ({
              label: role,
              value: role,
            }))}
          />
        </Form.Item>
        <Form.Item name="groupName" noStyle>
          <Input.Search
            placeholder="Group name..."
            style={{ width: 200 }}
            onSearch={() => searchFormProps.form?.submit()}
            allowClear
          />
        </Form.Item>
      </Space>
    </Form>
  )
}
